import Head from 'next/head';
import React, { useEffect, useRef, useState } from 'react';

import { clientHubTrack } from '@/components/client-hub/api';
import ClientHubMobileFooter from '@/components/client-hub/mobile-footer';
import ClientHubAccess from '@/components/client-hub/sections/access';
import ClientHubContact from '@/components/client-hub/sections/contact';
import ClientHubFaq from '@/components/client-hub/sections/faq';
import ClientHubPayment from '@/components/client-hub/sections/payment';
import ClientHubReferral from '@/components/client-hub/sections/referral';
import CanonicalURL from '@/components/common/Canonical';
import { BRAND_TITLE } from '@/utils/constants';

interface ClientHubProps {
  local: 'en' | 'fr';
  facilityData: any;
}

const ClientHub: React.FC<ClientHubProps> = ({ facilityData, local }) => {
  const pageVisitLogged = useRef(false);

  const hideNavRef = useRef(undefined);

  const [isMobile, setIsMobile] = useState(true);
  const [activeSection, setActiveSection] = useState('contact');

  const [showStoreId, setShowStoreId] = useState(false);

  const scrollTo = (
    elementId: 'contact' | 'payment' | 'access' | 'referral' | 'faq'
  ) => {
    if (isMobile) {
      setActiveSection(elementId);
    } else {
      const element = document.getElementById(elementId);
      window.scrollTo({
        top: element.offsetTop - 90,
        left: element.offsetLeft,
        behavior: 'smooth',
      });
    }
  };

  const setWebsiteFooter = (mobileDevice: boolean) => {
    const footerElements = document.getElementsByTagName('footer');
    if (footerElements.length) {
      const footer = footerElements[0] as HTMLElement;
      footer.style.display = mobileDevice ? 'none' : 'block';
    }
  };

  const track = (
    event:
      | 'Page visit'
      | 'Install app'
      | 'Call store'
      | 'Expand contact web form'
      | 'Submit contact web form'
      | 'Open MyAccount'
      | 'Open store page'
      | 'Open store directions'
      | 'Click Refer'
      | 'Open terms of service'
  ) => {
    // eslint-disable-next-line no-console
    console.log(`Tracking: ${event}`);
    if (event === 'Page visit') {
      pageVisitLogged.current = true;
    }
    const body = {
      userAgent: navigator.userAgent || '',
      isMobile,
      event,
      url: window.location.href || '',
      guid: 'none',
      isNewVisitor: true,
      dateOfFirstVisit: `${new Date()}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      timezoneOffset: String(new Date().getTimezoneOffset()),
      screenWidth: String(window.innerWidth),
      screenHeight: String(window.innerHeight),
    };
    clientHubTrack(body);
  };

  const setDeviceType = () => {
    const ua = window.navigator.userAgent.toLowerCase();
    const isMobileDevice =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        ua
      ) ||
      (navigator.platform === 'MacIntel' &&
        navigator.maxTouchPoints > 1 &&
        !(window as any).MSStream); // MacIntel for iPadOS
    setWebsiteFooter(isMobileDevice);
    setIsMobile(isMobileDevice);
  };

  // useEffect(() => {
  //   if ('serviceWorker' in navigator) {
  //     // register/sw.js must live at /sw.js in production
  //     window.addEventListener('load', () => {
  //       navigator.serviceWorker
  //         .register('/sw.js')
  //         .then((reg) => {
  //           console.log('ServiceWorker registered, scope:', reg.scope);
  //         })
  //         .catch((err) => {
  //           console.error('ServiceWorker registration failed:', err);
  //         });
  //     });
  //   }
  // }, []);

  useEffect(() => {
    setDeviceType();

    if (pageVisitLogged.current === false) {
      track('Page visit');
    }
  }, []);

  useEffect(() => {
    const hideTopRightSideNav = () => {
      if (hideNavRef.current) {
        clearInterval(hideNavRef.current);
      }
      hideNavRef.current = setInterval(() => {
        const rightSideNavElements =
          document.getElementsByClassName('rightSide-nav');
        if (rightSideNavElements.length) {
          const rightSideNav = rightSideNavElements[0] as HTMLElement;
          rightSideNav.style.display = 'none';
        }
      }, 500);
    };
    hideTopRightSideNav();

    return () => {
      if (hideNavRef.current) {
        clearInterval(hideNavRef.current);
        hideNavRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyPress = (event: any) => {
      if (event.shiftKey && event.key.toUpperCase() === 'L') {
        setShowStoreId((prev) => !prev);
      }
    };
    // Add event listener on mount
    window.addEventListener('keydown', handleKeyPress);
    // Cleanup the event listener on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  return (
    <div>
      <Head>
        <title>{local === 'fr' ? BRAND_TITLE.FR : BRAND_TITLE.EN}</title>
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        {/* <link rel="manifest" href="/pwa/manifest.json" /> */}
      </Head>
      <CanonicalURL />
      <div className="container">
        <div className="row justify-content-center g-3">
          <div className="column">
            <br />
            <div>
              <h2>
                {local === 'fr' ? 'Plateforme client ' : 'Client Hub'}
                {showStoreId && (
                  <span style={{ color: 'lightgrey' }}>
                    &nbsp;{facilityData?.Facility_ID}
                  </span>
                )}
              </h2>
            </div>
            <hr />
            <br />
            <ClientHubContact
              isMobile={isMobile}
              activeSection={activeSection}
              track={track}
              facilityData={facilityData}
              local={local}
            />
            <ClientHubPayment
              isMobile={isMobile}
              activeSection={activeSection}
              track={track}
              sitelinkId={facilityData?.Sitelink_URL_ID}
              storePhone={facilityData?.Support?.Phone_Number}
              local={local}
            />
            <ClientHubAccess
              isMobile={isMobile}
              scrollTo={scrollTo}
              activeSection={activeSection}
              track={track}
              sitelinkId={facilityData?.Sitelink_URL_ID}
              local={local}
            />
            <ClientHubReferral
              isMobile={isMobile}
              activeSection={activeSection}
              track={track}
              lcode={facilityData?.Facility_ID}
              local={local}
            />
            <ClientHubFaq
              isMobile={isMobile}
              activeSection={activeSection}
              track={track}
              scrollTo={scrollTo}
              sitelinkId={facilityData?.Sitelink_URL_ID}
              local={local}
            />
            {isMobile && (
              <ClientHubMobileFooter
                scrollTo={scrollTo}
                activeSection={activeSection}
                local={local}
              />
            )}
            <br /> <br /> <br /> <br />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientHub;
